import { TXsollaSubscriptionTier, VALID_SUBSCRIPTION_TIERS } from '../../../common/xsollaConfig';
import { TXsollaSubscriptionOperation, VALID_XSOLLA_SUBSCRIPTION_OPERATIONS } from '../../../common/xsolla';

/**
 * Persists an interrupted checkout selection (auth wall, expired session) so
 * the buyer's tier/billing choice survives the login round-trip. The premium
 * rail consumes it on mount and restarts checkout automatically. The intended
 * checkout operation IS persisted and gate-checked on resume: if subscription
 * state changed during the login round-trip so the re-derived operation no
 * longer matches, resume pre-selects the toggle but does not auto-open.
 */
export type TPendingCheckoutSelection = {
  tier: TXsollaSubscriptionTier;
  isAnnual: boolean;
  // The checkout operation the buyer intended when checkout was interrupted.
  // Undefined means a fresh purchase. Persisted so resume can refuse to
  // auto-open a DIFFERENT operation (e.g. a change_plan/downgrade) if
  // subscription state changed during the login round-trip.
  operation?: TXsollaSubscriptionOperation;
};

const STORAGE_KEY = 'rio-premium-pending-checkout';
// Long enough for a login round-trip, short enough that an abandoned buying
// session doesn't ambush the user with a payment widget on a later visit.
const MAX_AGE_MS = 10 * 60 * 1000;

export function savePendingCheckoutSelection(selection: TPendingCheckoutSelection): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...selection, savedAt: Date.now() }));
  } catch {
    // Storage unavailable (private mode, quota) — resume is best-effort only.
  }
}

/**
 * Drop any persisted selection. Call on terminal checkout outcomes (success /
 * settled-pending) so a completed purchase can never resurface a stale pending
 * choice and auto-open the payment widget on a later mount within MAX_AGE_MS.
 */
export function clearPendingCheckoutSelection(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

export function consumePendingCheckoutSelection(): TPendingCheckoutSelection | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      !VALID_SUBSCRIPTION_TIERS.includes(parsed.tier) ||
      typeof parsed.isAnnual !== 'boolean' ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > MAX_AGE_MS
    ) {
      return null;
    }

    const operation =
      parsed.operation === undefined || VALID_XSOLLA_SUBSCRIPTION_OPERATIONS.includes(parsed.operation)
        ? (parsed.operation as TXsollaSubscriptionOperation | undefined)
        : undefined; // unknown/corrupt operation degrades to fresh-purchase intent (safe: no wrong auto-open)

    return { tier: parsed.tier, isAnnual: parsed.isAnnual, operation };
  } catch {
    return null;
  }
}
